<template>
  <div class="atalho-dropdown-btn" @keydown.esc="showMenu = false">
    <button
      class="action-btn atalho-btn d-inline-flex align-items-center"
      :class="[colorClass, { 'disabled': disabled }]"
      @click="handleButtonClick"
      :aria-label="label + ' produção'"
      :title="disabled ? 'Cliente inativo - Dashboard indisponível' : label + ' produção'"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter.prevent="handleButtonClick"
      ref="btnRef"
      :disabled="disabled"
    >
      <i :class="icon" class="mr-2"></i>
      {{ label }}
      <span 
        class="atalho-dropdown-arrow" 
        @click.stop="handleArrowClick" 
        @keydown.enter.prevent="handleArrowClick"
        :tabindex="disabled ? -1 : 0" 
        :aria-label="disabled ? 'Dashboard indisponível' : 'Abrir opções'" 
        :aria-expanded="showMenu"
        v-if="!disabled"
      >
        <i class="fas fa-chevron-down" v-if="!openUp"></i>
        <i class="fas fa-chevron-up" v-else></i>
      </span>
      <span v-else class="atalho-dropdown-arrow disabled" :aria-label="'Dashboard indisponível'">
        <i class="fas fa-ban"></i>
      </span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'AtalhoDropdownBtn',
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: 'fas fa-link' },
    links: { type: Object, required: true }, // {prod, test, branch}
    color: { type: String, default: 'primary' },
    clienteId: { type: String, default: '' },
    categoria: { type: String, default: '' }, // dashboard, pwa, lp, configuracoes
    disabled: { type: Boolean, default: false }, // Novo prop para desabilitar o botão
  },
  data() {
    return { 
      showMenu: false, 
      openUp: false,
      portalElement: null
    };
  },
  computed: {
    colorClass() {
      return `bg-${this.color} text-white`;
    },
  },
  mounted() {
    this.$root.$on('close-all-dropdowns', this.handleCloseAll);
  },
  beforeDestroy() {
    this.$root.$off('close-all-dropdowns', this.handleCloseAll);
    this.removePortal();
  },
  methods: {
    handleButtonClick(e) {
      // Verificar se o botão está desabilitado antes de abrir o dropdown
      if (this.disabled) {
        return;
      }
      // Se clicou na seta, não fazer nada (a seta tem seu próprio handler)
      if (e.target.closest('.atalho-dropdown-arrow')) {
        return;
      }
      this.toggleMenu(e);
    },
    handleArrowClick(e) {
      // Verificar se o botão está desabilitado
      if (this.disabled) {
        return;
      }
      e.stopPropagation();
      this.toggleMenu(e);
    },
    abrir(tipo) {
      this.showMenu = false;
      this.removePortal();
      
      // Emitir evento para abrir link com carregamento automático
      if (this.clienteId) {
        this.$emit('abrir-link', { clienteId: this.clienteId, categoria: this.categoria, tipo: tipo });
      } else {
        // Fallback para quando não há clienteId
        const url = this.links[tipo];
        if (url) window.open(url, '_blank');
      }
    },
    toggleMenu(e) {
      if (this.disabled) {
        return;
      }
      
      if (!this.showMenu) {
        this.$root.$emit('close-all-dropdowns', this._uid);
        // Emitir evento para carregar atalhos quando abrir o dropdown
        if (this.clienteId) {
          this.$emit('carregar-atalhos', this.clienteId);
        }
      }
      this.showMenu = !this.showMenu;
      
      if (this.showMenu) {
        this.$nextTick(() => {
          this.createPortal();
        });
      } else {
        this.removePortal();
      }
    },
    createPortal() {
      this.removePortal(); // Remove portal anterior se existir
      
      const btn = this.$refs.btnRef;
      if (!btn) return;
      
      // Criar elemento do portal
      this.portalElement = document.createElement('div');
      this.portalElement.className = 'atalho-dropdown-menu-portal';
      this.portalElement.style.cssText = `
        position: fixed;
        z-index: 9999999;
        min-width: 170px;
        background: var(--table-bg);
        border-radius: 0.6rem;
        box-shadow: 0 8px 32px rgba(0,0,0,0.18);
        padding: 0.5em 0;
        animation: dropdownFadeIn 0.15s;
      `;
      
      // Criar conteúdo do dropdown baseado na categoria
      let dropdownContent = '';
      
      if (this.categoria === 'configuracoes') {
        // Dropdown específico para configurações (sem QR Code)
        dropdownContent = `
          <div class="dropdown-item" data-tipo="pwakraken">
            <div class="dropdown-item-content">
              <i class="fas fa-mobile-alt text-primary mr-1"></i> PWAKRAKEN
            </div>
          </div>
          <div class="dropdown-item" data-tipo="servidor">
            <div class="dropdown-item-content">
              <i class="fas fa-server text-info mr-1"></i> SERVIDOR
            </div>
          </div>
          <div class="dropdown-item" data-tipo="dashboard">
            <div class="dropdown-item-content">
              <i class="fas fa-tachometer-alt text-success mr-1"></i> DASHBOARD
            </div>
          </div>
          <div class="dropdown-item" data-tipo="qrbeta">
            <div class="dropdown-item-content">
              <i class="fas fa-qrcode text-warning mr-1"></i> QRBETA
            </div>
          </div>
          <div class="dropdown-divider"></div>
          <div class="dropdown-item" data-tipo="todos">
            <div class="dropdown-item-content">
              <i class="fas fa-list text-secondary mr-1"></i> Ver todos
            </div>
          </div>
        `;
      } else if (this.categoria === 'dashboard') {
        // Dropdown para dashboard (sem QR Code)
        dropdownContent = `
          <div class="dropdown-item" data-tipo="prod">
            <div class="dropdown-item-content">
              <i class="fas fa-check-circle text-success mr-1"></i> Produção
            </div>
          </div>
          <div class="dropdown-item" data-tipo="test">
            <div class="dropdown-item-content">
              <i class="fas fa-flask text-warning mr-1"></i> Homologação
            </div>
          </div>
          <div class="dropdown-item" data-tipo="branch">
            <div class="dropdown-item-content">
              <i class="fas fa-code-branch text-primary mr-1"></i> Selecionar branch
            </div>
          </div>
          <div class="dropdown-divider"></div>
          <div class="dropdown-item" data-tipo="local">
            <div class="dropdown-item-content">
              <i class="fas fa-laptop-code text-info mr-1"></i> Local (dev)
            </div>
          </div>
        `;
      } else {
        // Dropdown para PWA (com opção branch e local)
        dropdownContent = `
          <div class="dropdown-item" data-tipo="prod">
            <div class="dropdown-item-content">
              <i class="fas fa-check-circle text-success mr-1"></i> Produção
            </div>
            <i class="fas fa-qrcode qrcode-icon" data-tipo="prod" data-action="qrcode" title="Ver QR Code"></i>
          </div>
          <div class="dropdown-item" data-tipo="test">
            <div class="dropdown-item-content">
              <i class="fas fa-flask text-warning mr-1"></i> Homologação
            </div>
            <i class="fas fa-qrcode qrcode-icon" data-tipo="test" data-action="qrcode" title="Ver QR Code"></i>
          </div>
          <div class="dropdown-item" data-tipo="branch">
            <div class="dropdown-item-content">
              <i class="fas fa-code-branch text-primary mr-1"></i> Selecionar branch
            </div>
          </div>
          <div class="dropdown-divider"></div>
          <div class="dropdown-item" data-tipo="local">
            <div class="dropdown-item-content">
              <i class="fas fa-laptop-code text-info mr-1"></i> Local (dev)
            </div>
            <i class="fas fa-qrcode qrcode-icon" data-tipo="local" data-action="qrcode" title="Ver QR Code"></i>
          </div>
        `;
      }
      
      this.portalElement.innerHTML = dropdownContent;
      
      // Adicionar ao body primeiro para poder medir
      document.body.appendChild(this.portalElement);
      
      // Posicionar o dropdown
      this.positionDropdown();
      
      // Adicionar event listeners
      this.portalElement.addEventListener('click', this.handlePortalClick);
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscapeKey);
    },
    positionDropdown() {
      if (!this.portalElement || !this.$refs.btnRef) return;
      
      const btn = this.$refs.btnRef;
      const btnRect = btn.getBoundingClientRect();
      const menuRect = this.portalElement.getBoundingClientRect();
      const menuHeight = menuRect.height;
      const menuWidth = menuRect.width;
      
      // Calcular espaço disponível
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      
      // Decidir se abre para cima ou para baixo
      this.openUp = spaceBelow < menuHeight + 16 && spaceAbove > menuHeight + 16;
      
      // Calcular posição horizontal
      let left = btnRect.left;
      const rightEdge = left + menuWidth;
      
      // Se o menu ultrapassar a borda direita da tela, ajustar
      if (rightEdge > window.innerWidth) {
        left = window.innerWidth - menuWidth - 16; // 16px de margem
      }
      
      // Se o menu ultrapassar a borda esquerda da tela, ajustar
      if (left < 16) {
        left = 16;
      }
      
      // Calcular posição vertical
      let top;
      if (this.openUp) {
        top = btnRect.top - menuHeight - 8; // 8px de espaçamento
      } else {
        top = btnRect.bottom + 8; // 8px de espaçamento
      }
      
      // Garantir que não ultrapasse os limites da tela
      if (top < 16) {
        top = 16;
      }
      if (top + menuHeight > window.innerHeight - 16) {
        top = window.innerHeight - menuHeight - 16;
      }
      
      // Aplicar posição
      this.portalElement.style.top = `${top}px`;
      this.portalElement.style.left = `${left}px`;
    },
    handlePortalClick(e) {
      // Verificar se clicou no ícone de QR Code
      const qrcodeIcon = e.target.closest('.qrcode-icon');
      if (qrcodeIcon) {
        e.stopPropagation();
        const tipo = qrcodeIcon.dataset.tipo;
        this.mostrarQRCode(tipo);
        return;
      }

      // Verificar se clicou no conteúdo do item (não no ícone)
      const item = e.target.closest('.dropdown-item');
      if (item && !e.target.closest('.qrcode-icon')) {
        const tipo = item.dataset.tipo;
        this.abrir(tipo);
      }
    },
    mostrarQRCode(tipo) {
      // Fechar o dropdown
      this.showMenu = false;
      this.removePortal();
      
      // Emitir evento para o componente pai obter a URL e exibir o QR Code
      if (this.clienteId) {
        this.$emit('mostrar-qrcode', { 
          clienteId: this.clienteId, 
          categoria: this.categoria, 
          tipo: tipo 
        });
      }
    },
    handleOutsideClick(e) {
      if (this.portalElement && !this.portalElement.contains(e.target) && !this.$el.contains(e.target)) {
        this.showMenu = false;
        this.removePortal();
      }
    },
    handleEscapeKey(e) {
      if (e.key === 'Escape' && this.showMenu) {
        this.showMenu = false;
        this.removePortal();
      }
    },
    removePortal() {
      if (this.portalElement) {
        document.body.removeChild(this.portalElement);
        this.portalElement = null;
      }
      // Remover event listeners
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscapeKey);
    },
    handleCloseAll(uid) {
      if (uid !== this._uid) {
        this.showMenu = false;
        this.removePortal();
      }
    },
  },
};
</script>

<style scoped>
.atalho-dropdown-btn {
  position: relative;
  display: inline-block;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}
.atalho-btn {
  min-width: 180px;
  font-size: 1.08em;
  padding: 0.7em 1.3em 0.7em 1.3em;
  padding-right: 3.5em;
  border-radius: 0.8rem;
  font-weight: 500;
  outline: none;
  border: none;
  box-shadow: var(--shadow-qs-xs);
  transition: background 0.2s;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  pointer-events: auto;
}

.atalho-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--lp-switch-track-off) !important;
  color: var(--bootstrap-adb5bd) !important;
  pointer-events: none;
}

.atalho-btn.disabled .atalho-dropdown-arrow {
  pointer-events: none;
}

/* Garantir que botões não desabilitados tenham pointer-events auto */
.atalho-btn:not(.disabled) {
  pointer-events: auto;
}

.atalho-btn:not(.disabled) .atalho-dropdown-arrow {
  pointer-events: auto;
}

.atalho-btn.disabled:hover {
  background: var(--lp-switch-track-off) !important;
  transform: none !important;
}
.atalho-dropdown-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
  cursor: pointer;
  padding: 0.7em 0.8em;
  min-width: 32px;
  height: 100%;
  transition: all 0.2s;
  background: rgba(0, 0, 0, 0.2);
  position: absolute;
  right: 0;
  top: 0;
}
.atalho-dropdown-arrow:focus {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.atalho-dropdown-arrow:hover {
  background: rgba(0, 0, 0, 0.3);
  transform: scale(1.05);
}

.atalho-dropdown-arrow.disabled {
  background: rgba(0, 0, 0, 0.1);
  color: var(--bootstrap-adb5bd);
  cursor: not-allowed;
}

.atalho-dropdown-arrow.disabled:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: none;
}

@media (max-width: 600px) {
  .atalho-btn {
    min-width: 120px;
    font-size: 0.98em;
    padding: 0.5em 0.7em;
  }
}
</style>

<style>
/* Estilos globais para o dropdown do portal */
@keyframes dropdownFadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: none; }
}

.atalho-dropdown-menu-portal {
  z-index: 9999999 !important;
  position: fixed !important;
}

.atalho-dropdown-menu-portal .dropdown-item {
  padding: 0.7em 1.2em;
  color: var(--table-fg);
  font-size: 1em;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.atalho-dropdown-menu-portal .dropdown-item:hover {
  background: var(--qs-primary-muted);
}

.atalho-dropdown-menu-portal .dropdown-item-content {
  display: flex;
  align-items: center;
  flex: 1;
}

/* Itens sem QR Code (como configurações) não precisam de espaço extra */
.atalho-dropdown-menu-portal .dropdown-item:not(:has(.qrcode-icon)) .dropdown-item-content {
  width: 100%;
}

.qrcode-icon {
  margin-left: 0.5em;
  padding: 0.25em 0.5em;
  cursor: pointer;
  color: var(--lp-text-on-dark);
  transition: all 0.2s;
  font-size: 0.9em;
  opacity: 0.8;
}

.qrcode-icon:hover {
  opacity: 1;
  color: var(--lp-text-on-dark);
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.atalho-dropdown-menu-portal .dropdown-divider {
  height: 1px;
  background: var(--qs-border);
  margin: 0.5em 0;
}

@media (max-width: 600px) {
  .atalho-dropdown-menu-portal {
    min-width: 120px;
  }
}
</style> 